import { DealerSpaceParameterKey } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import {
  SPACE_DATE_RANGE_PANEL_VALUES,
  defineSpaceParameterPanelValues,
  firstSelectionToPanelValue,
  useSpaceParameterizedFilter,
} from '@src/components/AppLayout/SpaceParameters';
import { isExcludedByResolvedUsers, useResolvedUsers } from '@src/hooks/filters/useUsersFilter';
import { useCustomerUsers } from '@src/hooks/useCustomer';
import { useUser } from '@src/hooks/useUser';
import {
  EMPTY_OBJECT,
  FilterClauseType,
  IconName,
  PricingModeEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  isExcludedByCurrencyOrBasePairFilters,
  orderStatusFilterTextToBackendStatus,
  removeEmptyFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useDateRangeFilter,
  useMarketAccountsContext,
  useOrderStatusFilter,
  useSecuritiesContext,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type CustomerOrder,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type OrdStatusEnum,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isEqual, keys, startCase, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import {
  useAccountsFilter,
  useCounterpartyFilter,
  useIDFilter,
  useSidesFilter,
  useStrategiesFilter,
  useSubAccountsFilter,
  useUsersFilter,
} from '../../../hooks';
import { useMarketAccountsFilter } from '../../../hooks/filters/useMarketAccountsFilter';
import {
  CUSTOMER_WORKFLOW_FILTER_OPTIONS,
  getCustomerWorkflowFilterOptionLabel,
} from '../customerWorkflowFilterOptions';

export interface CustomerOrdersTableFilter extends DateRangeFilter {
  Counterparty?: string[];
  MarketAccounts?: string[];
  Sides?: string[];
  Symbols?: string[];
  Strategies?: string[];
  /** These are the order status texts we are filtering by on the client side */
  _statuses?: string[];
  /* These are the order statuses that we are filtering by on the server */
  Statuses?: OrdStatusEnum[];
  Users?: string[];
  CustomerUsers?: string[];
  SubAccounts?: string[];
  Accounts?: string[];
  OrderID?: string;
  ClOrdID?: string;
  RFQID?: string;
  PricingMode?: string[];
  Workflow?: string[];
  Currencies?: string[];
  BasePairs?: string[];
}

export interface UseCustomerOrdersFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.BasePairs]: 'BasePairs',
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
  [DealerSpaceParameterKey.DateRange]: SPACE_DATE_RANGE_PANEL_VALUES,
  [DealerSpaceParameterKey.MarketAccounts]: 'MarketAccounts',
  [DealerSpaceParameterKey.OrderID]: firstSelectionToPanelValue('OrderID'),
  [DealerSpaceParameterKey.PricingMode]: 'PricingMode',
  [DealerSpaceParameterKey.RFQID]: firstSelectionToPanelValue('RFQID'),
  [DealerSpaceParameterKey.Sides]: 'Sides',
  [DealerSpaceParameterKey.SubAccounts]: 'SubAccounts',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
  [DealerSpaceParameterKey.Users]: 'Users',
});
export const CUSTOMER_ORDER_SPACE_PARAMETER_KEYS = CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT.keys;

export function useCustomerOrdersFilter({ persistedBlotterTable }: UseCustomerOrdersFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const { Name: currentUserName } = useUser();
  const [initialFilter] = useState(() => cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter));
  const [localFilter, setFilter] = useState<CustomerOrdersTableFilter>(
    (initialFilter as CustomerOrdersTableFilter) || {}
  );
  const { marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();

  const changeLocalFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = localFilter;
      const newFilter = action instanceof Function ? action(localFilter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [localFilter, saveFilter]
  );
  const { changeFilter, controlledKeys, filter, getSelfManagedFilterBuilderProps } = useSpaceParameterizedFilter({
    filter: localFilter,
    changeFilter: changeLocalFilter,
    spaceParameterPanelValues: CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT,
  });
  const isDateRangeControlled = controlledKeys.includes(DealerSpaceParameterKey.DateRange);

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<CustomerOrder>>(
    row => {
      const { data } = row;
      if (filterExistsAndExcludes(filter, 'MarketAccounts', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Sides', data, 'Side')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Symbols', data, 'Symbol')) {
        return false;
      }
      if (
        isExcludedByCurrencyOrBasePairFilters(data?.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)
      ) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Strategies', data, 'Strategy')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'OrdStatus')) {
        return false;
      }
      if (isExcludedByResolvedUsers(resolvedUsers, data?.User)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'CustomerUsers', data, 'CustomerUser')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'PricingMode', data, 'PricingMode')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Workflow', data, 'Workflow')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'SubAccounts', data, 'SubAccount')) {
        return false;
      }
      if (filter.ClOrdID && filter.ClOrdID !== data?.ClOrdID) {
        return false;
      }

      if (filter.Accounts && filter.Accounts.length > 0) {
        const sourceAccountID = data ? marketAccountsByName.get(data.MarketAccount)?.SourceAccountID : undefined;
        if (sourceAccountID && !filter.Accounts?.includes(sourceAccountID)) {
          return false;
        }
      }
      return true;
    },
    [filter, marketAccountsByName, resolvedUsers, securitiesBySymbol]
  );

  const subAccountsFilter = useSubAccountsFilter(EMPTY_OBJECT);
  const usersFilter = useUsersFilter();
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer' });
  const symbolFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const marketAccountsFilter = useMarketAccountsFilter({ label: 'Customer Market Account' });
  const customerUsers = useCustomerUsers();
  const accountsFilter = useAccountsFilter();
  const strategiesFilter = useStrategiesFilter();
  const sidesFilter = useSidesFilter();
  const orderStatusFilter = useOrderStatusFilter();
  const idFilter = useIDFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof CustomerOrdersTableFilter>>([
        counterpartyFilter,
        marketAccountsFilter,
        symbolFilter,
        currencyOfSymbolFilter,
        basePairFilter,
        sidesFilter,
        strategiesFilter,
        orderStatusFilter,
        usersFilter,
        {
          key: 'CustomerUsers',
          label: 'Customer User',
          icon: IconName.User,
          options: customerUsers?.map(c => c.Email) ?? [],
          getOptionLabel: (option: string) => option,
          clearUnsupportedSelectionsOnInit: false,
        },
        {
          key: 'PricingMode',
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: startCase,
        },
        {
          key: 'Workflow',
          label: 'Workflow',
          icon: IconName.Puzzle,
          options: CUSTOMER_WORKFLOW_FILTER_OPTIONS,
          getOptionLabel: getCustomerWorkflowFilterOptionLabel,
        },
        subAccountsFilter,
        accountsFilter,
        { ...idFilter, key: 'OrderID', label: 'Order ID' },
        { ...idFilter, key: 'RFQID', label: 'RFQ ID' },
        { ...idFilter, key: 'ClOrdID', label: 'ClOrdID' },
      ]),
    [
      counterpartyFilter,
      marketAccountsFilter,
      symbolFilter,
      currencyOfSymbolFilter,
      basePairFilter,
      usersFilter,
      subAccountsFilter,
      customerUsers,
      accountsFilter,
      strategiesFilter,
      sidesFilter,
      orderStatusFilter,
      idFilter,
    ]
  );
  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof CustomerOrdersTableFilter)[]).forEach((key: keyof CustomerOrdersTableFilter) => {
        switch (key) {
          case '_start':
          case 'StartDate':
          case 'EndDate':
          case 'Statuses':
            return;
          default:
            clauses.push({
              key: key,
              type: FilterClauseType.INCLUSIVE,
              selections: filter[key] as string[],
            });
        }
      });
    }
    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: CustomerOrdersTableFilter = removeEmptyFilters<CustomerOrdersTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies CustomerOrdersTableFilter),
          OrderID: filterClausesByPropertyKey.get('OrderID')?.selections?.[0],
          RFQID: filterClausesByPropertyKey.get('RFQID')?.selections?.[0],
          ClOrdID: filterClausesByPropertyKey.get('ClOrdID')?.selections?.[0],
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () =>
      getSelfManagedFilterBuilderProps({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }),
    [getSelfManagedFilterBuilderProps, filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;
  const blotterTableFilterProps = useMemo(
    () => (isDateRangeControlled ? { showDateRangePicker: false } : dateRangeFilter),
    [dateRangeFilter, isDateRangeControlled]
  ) satisfies Partial<BlotterTableFiltersProps>;
  return {
    initialFilter,
    filter,
    clientSideFilter,
    changeFilter,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
    // shortcut to spread props into the BlotterTableFilters component
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Counterparty: 'Counterparty',
  MarketAccount: 'MarketAccounts',
  Side: 'Sides',
  Symbol: 'Symbols',
  Strategy: 'Strategies',
  OrdStatus: '_statuses',
  User: 'Users',
  CustomerUser: 'CustomerUsers',
  SubAccount: 'SubAccounts',
  marketAccount: 'Accounts',
  Workflow: 'Workflow',
  OrderID: 'OrderID',
  RFQID: 'RFQID',
  ClOrdID: 'ClOrdID',
} satisfies Partial<Record<keyof CustomerOrder | 'marketAccount', keyof CustomerOrdersTableFilter>>;
export function colIDToFilterBuilderKey(id: string): keyof CustomerOrdersTableFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}

interface CustomerOrdersServerFilter extends Pick<
  CustomerOrdersTableFilter,
  'StartDate' | 'EndDate' | 'OrderID' | 'RFQID'
> {
  Statuses?: string[];
  Counterparty?: string;
}

export function onlyServerFilterKeys(filter: CustomerOrdersTableFilter | undefined) {
  if (!filter) {
    return filter;
  }

  const serverFilter = {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
    Statuses: orderStatusFilterTextToBackendStatus(filter._statuses),
    OrderID: filter.OrderID,
    RFQID: filter.RFQID,
    Counterparty: filter.Counterparty?.at(0),
  } satisfies CustomerOrdersServerFilter;

  return serverFilter;
}
